import { useState } from 'react';
import { Layout, message, Modal, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import AddUserModal from '../UserModal/UserModal';
import { ReactComponent as Edit } from '../../../res/svg/edit.svg';
import { ReactComponent as Mail } from '../../../res/svg/mail.svg';
import User from '../../../models/User';
import {
  typesValue,
  stateValue,
  setColorValue,
} from '../../../utils/functions';
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks';
import { inviteUser, updateUser } from '../../../store/slice/userSlice';
import { selectRol } from '../../../store/slice/rolSlice';
import './UserTable.less';
/**
 * @component UserTable.tsx
 * @description UserTable component to show the user list.
 * @return { Component }
 */
interface UserTableProps {
  userList: User[];
  userDeleted: (user: User) => void;
  userUpdate: (user: User) => void;
  updatedListUser: () => void
}
function UserTable(props: UserTableProps) {
  const dispatch = useAppDispatch();
  const { Text } = Typography;
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [userEdit, setUserEdit] = useState<User>({});
  const rolState = useAppSelector(selectRol);
  const editModalOnClick = (item: User) => {
    if (item) {
      setUserEdit(item);
      setShowModalEdit(true);
    }
  };
  const sendEmailToUser = (userId: string) => {
    dispatch(inviteUser(userId)).then((value: any) => {
      if (value.payload.status) {
        message.success('Invitación enviada satisfactoriamente');
        //Actualiza lista de usuarios
        props.updatedListUser();
      } else {
        message.error(value.payload.data.message);
      }
    });
  };
  const sendEmailOnClick = (item: User) => {
    Modal.info({
      title: 'Invitación enviada!',
      content: (
        <div>
          <p>
            El usuario deberá aceptarla dentro de las próximas 24 horas o
            caducará.{' '}
          </p>
          <p className="org-user-table__title_modal">
            ¿Qué pasa si la invitación caduca?
          </p>
          <p>
            No te preocupes, podrás enviar una nueva invitación desde la sección
            de{' '}
            <a href=" " className="org-user-table__href_modal">
              Usuarios.
            </a>
          </p>
        </div>
      ),
      onOk() {
        sendEmailToUser(item.id!);
      },
      okText: 'Aceptar',
    });
  };
  const isHidden = (_isHidden: boolean, user: User) => {
    props.userDeleted(user);
    setShowModalEdit(_isHidden);
  };
  const sorter: any = (a:any, b:any) => (isNaN(a) && isNaN(b) ? (a || '').localeCompare(b || '') : a - b);
  const columns: ColumnsType<User> = [
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      sortDirections: ['descend', 'ascend'],
      sorter: (a:User, b: User) => sorter(a.nombre, b.nombre),
      responsive: ["xl"]
    },
    {
      title: 'Apellido',
      dataIndex: 'apellido',
      key: 'apellido',
      sorter: (a: User, b: User) => sorter(a.apellido, b.apellido),
      responsive: ["xl"]
    },
    {
      title: 'DNI',
      dataIndex: 'dni',
      key: 'dni',
      responsive: ["xl"]
    },
    {
      title: 'Correo Electrónico',
      dataIndex: 'email',
      key: 'email',
      responsive: ["xl"]
    },
    {
      title: 'N° Telefónico',
      dataIndex: 'celular',
      key: 'celular',
    },
    {
      title: 'Acceso',
      dataIndex: ['accesoGestion', 'accesoAdministracion', 'accesoRegistros'],
      key: 'acceso',
      sorter: (a: User, b: User) => a.tipo! - b.tipo!,
      render: (_text,row:any) => <>{stateValue({accesoGestion: row["accesoGestion"],accesoAdministracion: row["accesoAdministracion"],accesoRegistros: row["accesoRegistros"],accesoCatastro: row["accesoCatastro"]})}</>,
    },
    {
      title: 'Rol',
      key: 'rol',
      dataIndex: 'rol',
      sorter: (a: User, b: User) => a.rolId! - b.rolId!,
      render: (_, { rol }) => (
        <>
          {rol !== null &&
            [rol].map((item: any) => {
              return <Text key={item}>{item.rol}</Text>;
            })}
        </>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      sorter: (a: User, b: User) => a.estado! - b.estado!,
      render: (id, row) => <Tag className={setColorValue(id,row.isVencido!)}>{typesValue(id,row)}</Tag>,
      responsive: ["xl"]
    },
    {
      title: 'Acciones',
      key: 'action',
      render: (item, row) => (
        <Space size="large" className="icon-edit">
          <Edit onClick={() => editModalOnClick(item)} />
          {row.estado !== 1 && row.estado !== 2 && <Mail onClick={() => sendEmailOnClick(item)} />}
        </Space>
      ),
    },
  ];
  const userValue = (user: User) => {
    user.id = userEdit.id;
    user.rol = user.rolId ? rolState.rolesList.find((x: any) => x.id === user.rolId) : {};
    user.estado = user.check === undefined ? 0 : user.check ? 1 : 2;
    user.accesoAdministracion = user.tipo.includes("accesoAdministracion") ? true : false
    user.accesoGestion = user.tipo.includes("accesoGestion") ?  true : false
    user.accesoCatastro = user.tipo.includes("accesoCatastro") ?  true : false
    user.accesoRegistros = user.tipo.includes("accesoRegistros") ? true : false
    dispatch(updateUser(user)).then((value: any) => {
      if (value.payload.status) {
        message.success('Usuario editado con exito.');
        props.userUpdate(user);
        setShowModalEdit(false);
      } else {
        message.error('Error : ' + value.payload.data.message);
      }
    });
  };

  return (
    <Layout className="org-user-table__main">
      <AddUserModal
        buttonText="Crear usuario"
        user={userEdit}
        setUser={userValue}
        isModalVisible={showModalEdit}
        isHidden={isHidden}
      />
      <Table
        rowKey="id"
        columns={columns}
        dataSource={props.userList}
        sortDirections={['descend', 'ascend']}
        locale={{ emptyText: 'Sin datos'}}
        pagination={{locale: {items_per_page : '/ página'}, className: 'org-user-table__pagination'}}
      />
    </Layout>
  );
}

export default UserTable;
